import React, { useEffect, useRef, useState } from 'react';

import './GameNameEditable.css';


// Matches Game.name's max_length on the backend
// (hsh_python/games/models.py:234, STANDARD_CHARFIELD_LENGTH = 254).
const MAX_NAME_LENGTH = 254;

// Model default (hsh_python/games/models.py:235). Treated as "no real
// name set" alongside empty/null, so the visible placeholder shows
// `Game ${gameId}` instead of the default string.
const DEFAULT_GAME_NAME = 'My Game';


function _isPlaceholderName(name) {
    if (name === null || name === undefined) return true;
    const trimmed = String(name).trim();
    return trimmed === '' || trimmed === DEFAULT_GAME_NAME;
}


export default function GameNameEditable(
    { gameId, name, createdById, currentUserId, onRename }) {
    // CC_GAME_NAMING: owner-editable game name. One control, two mount
    // points (lobby header + saved-games modal row). Non-owners get a
    // read-only span that never enters edit mode.
    //
    // Ownership gates on the stable integer user id, never a username
    // string — the two mounts feed created_by differently (the list's
    // GameHotSerializer exposes created_by_id; the live game push's
    // GameSerializer renders the created_by FK as its pk), so the
    // numeric id is the only thing both can supply, and it matches the
    // backend authority (rename_game.py compares created_by_id).
    const isOwner = createdById != null && createdById === currentUserId;

    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState('');
    // Optimistic name held after a commit. The rename is fire-and-forget;
    // the lobby reconciles via the server game push, but the saved-games
    // list only refetches on demand — so without this the row would snap
    // back to the stale `name` prop until a manual refresh. Cleared once
    // the incoming `name` prop changes (server/refresh caught up).
    const [pendingName, setPendingName] = useState(null);
    // Guards the edit session so a commit/cancel only fires once even
    // though both keydown and the follow-on blur (input unmounts) reach
    // the same handler.
    const finishingRef = useRef(false);
    // The value the editor opened with; an unchanged commit is a no-op so
    // clicking in and out (or committing the placeholder verbatim) never
    // writes a spurious rename.
    const initialValueRef = useRef('');

    useEffect(() => {
        // A fresh `name` from the parent is now authoritative; drop the
        // optimistic override so external renames/refreshes win.
        setPendingName(null);
    }, [name]);

    const effectiveName = pendingName != null ? pendingName : name;
    const isPlaceholder = _isPlaceholderName(effectiveName);
    const displayName = isPlaceholder ? `Game ${gameId}` : effectiveName;

    const startEdit = () => {
        if (!isOwner) return;
        // Pre-fill the editor with what's currently shown, so the owner
        // edits the existing name instead of starting from a blank field.
        setValue(displayName);
        initialValueRef.current = displayName;
        finishingRef.current = false;
        setEditing(true);
    };

    const commit = () => {
        if (finishingRef.current) return;
        finishingRef.current = true;
        const trimmed = value.trim();
        setEditing(false);
        if (trimmed && trimmed !== initialValueRef.current) {
            setPendingName(trimmed);
            onRename(trimmed);
        }
    };

    const cancel = () => {
        if (finishingRef.current) return;
        finishingRef.current = true;
        setEditing(false);
    };

    const handleChange = (event) => {
        setValue(event.target.value.slice(0, MAX_NAME_LENGTH));
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            commit();
        } else if (event.key === 'Escape') {
            event.preventDefault();
            cancel();
        }
    };

    if (editing) {
        return (
            <input
                className="game-name-editable-input"
                type="text"
                aria-label="Game name"
                value={value}
                maxLength={MAX_NAME_LENGTH}
                autoFocus
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onBlur={commit}
            />
        );
    }

    return (
        <span
            className={isOwner
                ? 'game-name-editable-display game-name-editable-owner'
                : 'game-name-editable-display'}
            title={isOwner ? 'Click to rename' : undefined}
            onClick={startEdit}
        >
            {displayName}
        </span>
    );
}
